/**
 * XP, niveaux, Points de Créativité, paliers et exigence d'Atelier — SPEC-MOTEUR §9.
 *
 * Les paliers sont des **récompenses, jamais des gates** (PLAN §4.13) : franchir un palier
 * crédite des PC et durcit l'Atelier, il ne verrouille rien.
 */
import { CHARGES_FIXES_PAR_JOUR, PALIER_PRODUCTION_VALUE, PRODUCTION_VALUE_PRIX_PLEIN, } from "./actions.js";
import { SYSTEMES_TOUS_ACTIFS } from "./menage.js";
import { PALIERS, PALIERS_POST_1M, } from "./types.js";
// ─────────────────────────────────────────────────────────────────────────────
// §9.1 XP et niveaux
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Constante du seuil de niveau : `seuil cumulé du niveau n = 150 × n³` XP.
 *
 * Elle vise ~45 niveaux par run (PLAN §4.12) et elle est **recalibrable** par
 * `simulate.ts` (M3.12) — contrairement aux ancres de §12, qui sont intouchables.
 */
export const COEF_NIVEAU = 150;
/** Rétention d'une vidéo : `clamp(qualite_eff / 1,6 ; 0,2 ; 1,0)` (§9.1). */
export function retention(qualiteEff) {
    return Math.min(1.0, Math.max(0.2, qualiteEff / 1.6));
}
/** Seuil d'XP **cumulée** pour atteindre le niveau `n` (§9.1). Niveau 1 = 0 XP. */
export function seuilNiveau(n) {
    if (n <= 1)
        return 0;
    return COEF_NIVEAU * Math.pow(n, 3);
}
/** Niveau atteint pour une XP cumulée donnée — inverse de `seuilNiveau`. */
export function niveauPourXp(xp) {
    if (xp < seuilNiveau(2))
        return 1;
    // n = floor((xp / 150)^(1/3)), affiné pour absorber l'imprécision de cbrt.
    let n = Math.max(1, Math.floor(Math.cbrt(xp / COEF_NIVEAU)));
    while (seuilNiveau(n + 1) <= xp)
        n++;
    while (n > 1 && seuilNiveau(n) > xp)
        n--;
    return n;
}
// ─────────────────────────────────────────────────────────────────────────────
// §9.2 Paliers et exigence d'Atelier
// ─────────────────────────────────────────────────────────────────────────────
/** Tous les paliers, dans l'ordre : les 6 de la run + les 3 de « Après la rue ». */
export const TOUS_PALIERS = [...PALIERS, ...PALIERS_POST_1M];
/**
 * ⟦C62 · A-179⟧ Le palier du MILLION : le dernier des paliers de la run (`PALIERS`), celui qui porte la victoire.
 * Même valeur que `OBJECTIF_ABONNES` (`index.ts`), relue ici sans l'importer (`index.ts` importe ce module).
 */
export const PALIER_MILLION = PALIERS[PALIERS.length - 1];
/** PC crédités par palier franchi, y compris post-1M (PLAN §4.12/§4.13). */
export const PC_PAR_PALIER = 2;
/** PC crédités par niveau gagné (PLAN §4.12). */
export const PC_PAR_NIVEAU = 1;
/** PC crédités par événement majeur survécu (§9.3). */
export const PC_PAR_EVENEMENT_MAJEUR = 1;
/**
 * PC crédités par vidéo jalon réussie (§9.3).
 *
 * ⚠️ **Source de vérité UNIQUE, et ça se mesurait à l'écran** : le joueur touchait **+4 PC**,
 * parce que les dix jalons de `content/milestones.json` portaient en plus un `pc_add: 2` dans
 * leurs `effets_reussite.autres`, appliqué par `applyEffectsInPlace`, **et** que `jouerJalon`
 * appelait `crediterJalon`. « +2 PC par jalon réussi » est une règle de **moteur**, uniforme
 * pour les dix (§9.3) : elle vit ici, le contenu ne la redit plus, et le test de cohérence
 * **T7 bis** rougit si un `pc_add` réapparaît dans un jalon.
 */
export const PC_PAR_JALON = 2;
/** Un jalon est réussi à partir de ce score d'exécution (§9.3). */
export const SEUIL_JALON_REUSSI = 60;
/**
 * `k` = nombre de paliers franchis **parmi les 5 premiers**, plafonné à 5 (§9.2).
 * Les paliers post-1M ne montent pas `k` : « au plafond » y compris après la rue.
 */
export function exigenceK(state) {
    const cinqPremiers = PALIERS.slice(0, 5);
    const franchis = cinqPremiers.filter((p) => state.subscribers >= p).length;
    const parHistorique = state.milestones_reached.filter((p) => cinqPremiers.includes(p)).length;
    return Math.min(5, Math.max(franchis, parHistorique));
}
// ─────────────────────────────────────────────────────────────────────────────
// ⟦A-9⟧ Le plafond de compétence — « bien joué, mais pas encore assez bon »
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Le plafond sépare **ton geste** (le mini-jeu, honnête, jamais saboté) de **ce que ton
 * geste vaut** (ton niveau). Affichage visé : « Écriture — 92 · plafonné à 45, ton niveau ».
 *
 * **Pourquoi pratique + arbre, et surtout pas l'arbre seul.** Mesuré : un plafond piloté par
 * l'arbre OU par les paliers **se verrouille lui-même** — il écrase la qualité ⇒ moins
 * d'abonnés et moins de PC ⇒ ni nœuds ni paliers ⇒ le plafond ne monte jamais (1 nœud acheté
 * sur 39 en 700 actions, 100 abonnés jamais atteints). **La pratique est la seule chose qu'un
 * joueur bridé peut encore faire** : elle porte donc le socle, l'arbre ne fait qu'ajouter.
 *
 * **État neutre par défaut** (CLAUDE.md, ⟦A-10⟧) : `55 + 30 + 15 = 100`. À l'arbre complet et
 * après pratique, le plafond ne plafonne plus rien — aucune ancre de calibrage ne bouge. Une
 * mécanique dont le défaut n'est pas neutre fausse le modèle en silence, et c'est précisément
 * ce que le tuteur d'A-10 doit rester capable de détecter.
 */
export const PLAFOND_SOCLE = 55; // ✎
/** Ce que la PRATIQUE ajoute au socle, en totalité (✎ A-9). */
export const PLAFOND_GAIN_PRATIQUE = 30; // ✎
/**
 * Publications au bout desquelles la pratique a tout donné (✎).
 *
 * A-9 disait « +30 sur 30 vidéos » ; le complément du 14/08 **recalibre sur ~10** pour tenir
 * la cible de Mathieu : « à la cinquième vidéo, il faut que tu sentes que tu n'es pas loin ;
 * à la huitième ou dixième, tu as passé le 100 ».
 */
export const PLAFOND_PRATIQUE_VIDEOS = 10; // ✎
/** Ce que l'ARBRE de la branche concernée ajoute, à branche complète (✎ A-9). */
export const PLAFOND_GAIN_ARBRE = 15; // ✎
/**
 * ⟦A-11 §2⟧ **Le plafond chez ses parents** — le prix du retour, et il ne se paie pas en
 * Clicks : *« les charges paient le studio ; sans studio, on tourne dans une chambre
 * d'enfant »*. Le plafond retombe **au socle**, lecture littérale de l'avenant (« le plafond
 * de compétence retombe au niveau de base ») et miroir exact de la production value (§3).
 *
 * ✎ **55, et c'est ce chiffre-là qui fait s'auto-équilibrer la mécanique** — A-11 §2 le
 * demande sans garde-fou artificiel, et la seule table du plafond suffit à le produire :
 *
 * | Qui rentre | Plafond avant | Après | Ce qu'il perd |
 * |---|---|---|---|
 * | Un débutant acculé (0 publication, pas d'arbre) | 55 | 55 | **rien** |
 * | Juste après le Grand Saut (~40 publications, arbre à peine entamé) | 85 | 55 | 30 |
 * | Une grosse chaîne (pratique pleine, branche complète) | 100 | 55 | **45** |
 *
 * « Le calcul ne tient que quand on est petit et acculé » n'est donc pas une règle écrite
 * quelque part : c'est ce que la table dit d'elle-même. Une grosse chaîne qui rentrerait
 * saborderait sa production pour économiser 5 ₵/jour.
 *
 * C'est un **plafond**, jamais un malus soustrait : il ne peut donc que rabaisser, et un
 * joueur déjà au socle ne perd rien. `false`/`undefined` ⇒ aucun effet (⟦A-10⟧).
 */
export const PLAFOND_CHEZ_LES_PARENTS = PLAFOND_SOCLE; // ✎
/**
 * ⟦A-11 §3⟧ Ce que le **plein tarif** de production value ajoute au plafond — « le plafond du
 * plafond » de l'avenant.
 *
 * ✎ **15, exactement `PLAFOND_GAIN_ARBRE`**, et ce n'est pas une coïncidence : c'est ce qui
 * rend « l'argent achète du potentiel, jamais du score » **arithmétiquement vrai**. Socle 55
 * + pratique 30 = 85 ; les 15 derniers points s'achètent soit avec l'arbre, soit avec de
 * l'argent, jamais au-delà — le total reste borné à 100, qui est le maximum de l'échelle des
 * scores d'Atelier (§1.2). L'argent **remplace** un arbre incomplet, il ne s'y ajoute pas :
 * un joueur à l'arbre complet n'a littéralement rien à acheter.
 *
 * Conséquence voulue pour ⟦A-11 §2⟧ : depuis le socle 55 (chez ses parents), le plein tarif
 * ne remonte qu'à 70. **On ne rachète pas son studio à la production value** — le retour
 * chez les parents reste une punition, pas une optimisation.
 */
export const PLAFOND_GAIN_PRODUCTION_VALUE = 15; // ✎
/**
 * ⟦A-33⟧ **Ce qui passe au-dessus du plafond n'est plus jeté, il est compressé.**
 *
 * Mathieu, après avoir joué : *« Ce n'est pas le score qui doit être réduit, il doit y avoir
 * des calculs qui vont faire varier des choses. La note des résultats, c'est une des
 * constantes qui est utilisée dans les calculs. »*
 *
 * Le défaut mesuré de la coupe franche : avec un plafond à 70, **un 98 et un 71 rendaient
 * exactement la même chose**. Au-dessus de son plafond, le joueur n'avait plus aucune raison
 * de bien jouer — le pire défaut possible dans un jeu d'adresse.
 *
 *     score_effectif = plafond + (score − plafond) × K   si score > plafond
 *     score_effectif = score                             sinon
 *
 * ✎ **⟦A-47⟧ Mathieu a tranché la valeur forte : `K = 0,60`.** ⟦A-33⟧ avait livré la mécanique
 * à l'état neutre `K = 0`, où la formule redevient `min(score, plafond)` au bit près — donc
 * inactive : le défaut ci-dessus était réparé en théorie et intact à l'écran.
 *
 * Les trois valeurs mesurées (0,25 / 0,40 / 0,60, 625 runs chacune) sont en
 * `docs/AUDIT-EQUILIBRAGE.md §13`. Ce qui a décidé 0,60 : **les 100 premiers abonnés tombent
 * en 8 vidéos au lieu de 10** — le bas de la cible de Mathieu — et c'est le seul mouvement
 * franc de toute la campagne ; les valeurs intermédiaires ne déplacent presque rien.
 *
 * 🔑 **C'est un levier d'ARC 1, pas un levier de fin de partie**, et c'est délibéré : à
 * 0 publication le plafond vaut 55 contre une exécution forte à N(88 ; 5), donc l'écart est
 * maximal ; après dix publications la pratique l'a monté à 85, l'écart tombe à ~3 points et
 * l'effet s'évanouit de lui-même. Il récompense l'adresse **là où le joueur découvre le jeu**,
 * sans rien changer à la suite — la sortie de 1 k → 10 k reste identique aux quatre valeurs.
 *
 * ⚠️ **Toute lecture d'écran de cette valeur passe par `scoreEffectifAtelier`**, jamais par le
 * plafond brut : au-dessus du plafond, ce que le moteur retient est PLUS GRAND que lui. Le
 * préalable d'⟦A-47⟧ (client + `server/src/atelier/patterns.ts`) a été livré **avant** ce
 * réglage, précisément pour que l'écran ne se remette pas à mentir ⟦A-32⟧.
 *
 * Deux propriétés qui font que ce levier **ne peut pas ré-inverser** la corrélation
 * compétence → réussite rétablie par ⟦A-22⟧ :
 *
 *  1. La fonction est **monotone croissante** en `score` pour tout `K ≥ 0` : un geste meilleur
 *     ne rend jamais moins qu'un geste moins bon.
 *  2. Elle est **inerte sous le plafond** : un joueur qui ne l'atteint pas ne reçoit rien.
 *     Le socle valant 55, un profil d'exécution faible (loi N(45 ; 10)) est quasi intouché,
 *     alors qu'un profil fort (N(88 ; 5)) récupère la quasi-totalité de son dépassement.
 *     **Le levier ne peut que creuser l'écart dans le bon sens.**
 */
export const K_SURPASSEMENT = 0.6; // ✎ ⟦A-47⟧ la valeur forte, tranchée par Mathieu (0 = A-9)
/**
 * Borne **DURE** de l'échelle des scores d'Atelier — 85 en mode tolérance (PLAN §4.5), 100
 * sinon (§1.2). Rien ne la franchit, pas même le surpassement d'⟦A-33⟧ : ce n'est pas un
 * plafond de compétence qu'on peut dépasser par l'adresse, c'est **la borne de l'échelle**
 * (et, pour la tolérance, une contrepartie assumée de fenêtres élargies ×1,30).
 *
 * Exportée pour n'exister qu'à un seul endroit : `exigenceAtelier` et `scoreEffectifAtelier`
 * la lisent toutes deux, et une constante recopiée dérive (SPEC-MOTEUR, surface publique).
 */
export const CAP_TOLERANCE = 85;
/** La borne dure de l'échelle pour cet état — voir `CAP_TOLERANCE`. */
export function capEchelle(state) {
    return state.flags.accessibility_tolerance === true ? CAP_TOLERANCE : 100;
}
/**
 * Courbe **prix → plafond** de la production value ⟦A-11 §3⟧ : `gain × √(prix / plein tarif)`,
 * bornée au plein tarif.
 *
 * ✎ **La racine, et pas une droite** : les premiers Clicks doivent se sentir, les derniers
 * doivent se payer cher. Avec 500 ₵ de plein tarif et 15 points de gain :
 *
 * | Prix | Bonus de plafond |
 * |---|---|
 * | 0 ₵ | **+0** (l'état neutre) |
 * | 50 ₵ | +4,7 |
 * | 125 ₵ | +7,5 — **un quart du prix achète la moitié du bonus** |
 * | 250 ₵ | +10,6 |
 * | 500 ₵ | +15 (plein tarif) |
 *
 * Le joueur qui n'a pas les moyens du plein tarif n'est donc jamais exclu : il achète la
 * moitié de l'effet pour un quart du prix. Et celui qui a les moyens ne gagne pas la partie
 * pour autant — il gagne le **droit de la jouer** à son meilleur niveau.
 *
 * Pure : aucun état lu au-delà des abonnés, aucun aléa. Rend 0 hors du palier — mais
 * `validateAction` refuse déjà l'action dans ce cas, précisément pour ne jamais débiter des
 * Clicks qui n'achèteraient rien.
 */
export function bonusProductionValue(state, prix) {
    if (!(prix > 0))
        return 0;
    const ouverte = state.subscribers >= PALIER_PRODUCTION_VALUE ||
        state.milestones_reached.includes(PALIER_PRODUCTION_VALUE);
    if (!ouverte)
        return 0;
    const part = Math.min(1, Math.sqrt(prix / PRODUCTION_VALUE_PRIX_PLEIN));
    return PLAFOND_GAIN_PRODUCTION_VALUE * part;
}
/**
 * La branche d'arbre qui porte chaque étape d'Atelier — c'est ce qui rend « la CTA est
 * meilleure » **littéral** (A-9). Les cinq étapes du pipeline (§1.2) répondent aux cinq
 * mini-jeux (PLAN §4.5), et chacun est adossé à une branche (PLAN §4.12) :
 *
 * | Étape | Mini-jeu | Branche |
 * |---|---|---|
 * | écriture | Le script qui tombe (⟦C52⟧, avant : Le fil rouge) | Écriture |
 * | tournage | La prise parfaite (⟦C53⟧, ex-« La bonne prise ») | Tournage |
 * | montage | Couper dans le rythme | Montage |
 * | miniature | Le putaclic-o-mètre | **Algorithme** (`miniature_putaclic`, `MAJUSCULES`, `SEO`) |
 * | promotion | La Découpe | **Charisme** (pousser sa vidéo, c'est se montrer) |
 *
 * ⚠️ Les deux dernières lignes sont un **choix de design ✎ à confirmer par Mathieu** : la
 * lecture par les `assists` du contenu est ambiguë (La Découpe est assistée par Business ET
 * Algorithme). La **sixième branche, Business, ne plafonne rien** — c'est l'argent, elle ne
 * touche pas à l'Atelier. Table structurelle (5 étapes ↔ 5 branches), jamais une règle de
 * contenu : ce sont les nœuds eux-mêmes, dans `content/skills.json`, qui disent leur branche.
 */
export const BRANCHE_DU_STAGE = {
    ecriture: "ecriture",
    tournage: "tournage",
    montage: "montage",
    miniature: "algorithme",
    promotion: "charisme",
};
/** Vue élargie d'un contenu qui porte peut-être des branches. */
function noeudsDeLaBranche(content, branche) {
    return (content?.skills ?? []).filter((s) => s.branche === branche);
}
/**
 * Part de la branche possédée, dans `[0;1]`.
 *
 * **Branche absente du contenu ⇒ 1, pas 0.** C'est l'état neutre : un monde où cette branche
 * n'existe pas ne peut pas reprocher au joueur de ne pas l'avoir achetée. Sans cette règle,
 * le moindre contenu de test sans arbre rabaisserait silencieusement le plafond à 85 — soit
 * exactement la dérive invisible que ⟦A-10⟧ demande de rendre impossible.
 */
export function partArbreAtelier(state, stage, content) {
    const noeuds = noeudsDeLaBranche(content, BRANCHE_DU_STAGE[stage]);
    if (noeuds.length === 0)
        return 1;
    const possedes = new Set(state.skills);
    return noeuds.filter((n) => possedes.has(n.id)).length / noeuds.length;
}
/** Part de la pratique acquise, dans `[0;1]` — les publications, rien d'autre (⟦A-9⟧). */
export function partPratiqueAtelier(state) {
    if (PLAFOND_PRATIQUE_VIDEOS <= 0)
        return 1;
    return Math.min(1, state.counters.videos_published / PLAFOND_PRATIQUE_VIDEOS);
}
/**
 * Le plafond de compétence d'une étape d'Atelier, dans `[PLAFOND_SOCLE;100]`.
 *
 * Sans `content`, la part d'arbre est neutre (branche inconnue = branche inexistante) :
 * l'appelant qui ne passe pas le contenu obtient un plafond qui ne dépend que de la
 * pratique, jamais un plafond faussement bas.
 *
 * ⟦A-11⟧ Deux leviers s'ajoutent, dans cet ordre — et l'ordre porte tout le sens :
 *
 *  1. **§3, la production value** monte le plafond de la vidéo en cours ;
 *  2. **§2, le retour chez les parents** le rabat ensuite au socle.
 *
 * Le plafond des parents s'applique **en dernier**, donc il gagne : on ne rachète pas son
 * studio à coups de production value. Le maximum reste 100, borne de l'échelle des scores.
 */
export function plafondCompetence(state, stage, content, options = {}) {
    const brut = PLAFOND_SOCLE +
        PLAFOND_GAIN_PRATIQUE * partPratiqueAtelier(state) +
        PLAFOND_GAIN_ARBRE * partArbreAtelier(state, stage, content) +
        bonusProductionValue(state, options.production_value ?? 0);
    // ⟦A-11 §2⟧ Sans studio, on tourne dans une chambre d'enfant — quoi qu'on ait payé.
    const plafondDuLieu = state.flags.chez_les_parents === true ? PLAFOND_CHEZ_LES_PARENTS : 100;
    return Math.min(100, plafondDuLieu, Math.round(brut));
}
/**
 * Exigence d'Atelier au palier courant — table **cumulée** de §9.2 :
 * tempo `1,08^k`, fenêtres `0,90^k`, cuts `1,20^k`.
 *
 * Deux modificateurs de fenêtre se cumulent avec la table (§9.2) :
 * - **mode doux** ×1,30 sous 100 abonnés (k = 0),
 * - **tolérance accessibilité** ×1,30 et score plafonné à 85 (PLAN §4.5).
 *
 * ⟦A-9⟧ `score_cap` porte en plus le **plafond de compétence** de l'étape — mais **seulement
 * si `stage` est fourni**. Sans étape, la question « plafonné à combien ? » n'a pas de
 * réponse (le plafond est par branche) : on rend alors la seule borne qui ne dépend de rien,
 * celle de la tolérance. C'est ce qui garde l'appel historique `exigenceAtelier(state)`
 * exactement aussi neutre qu'avant.
 */
export function exigenceAtelier(state, stage, content, options = {}) {
    const k = exigenceK(state);
    // ⟦A-14 §1⟧ **Le premier palier est DOUX.** L'exposant appliqué aux multiplicateurs est
    // `E − 1`, pas `E` : franchir 100 abonnés ne durcit RIEN, le durcissement commence à 1 000.
    //
    // Deux raisons, tranchées par Mathieu le 14/08 : la table SPEC-UI §4.2 est la source
    // désignée des valeurs de base ⟦A12⟧ et ses colonnes sont indexées par **E** — l'autre
    // lecture les décalerait toutes, donc la table de référence deviendrait fausse ; et ⟦A-9⟧,
    // à 100 abonnés le joueur *vient de finir d'apprendre*, le durcir à cet instant précis le
    // punit au moment où il croit enfin maîtriser. **SPEC-MOTEUR §9.2 est à amender.**
    //
    // `k` reste le **niveau d'exigence E** (paliers franchis) : c'est lui que lisent les
    // générateurs de patterns pour indexer la table §4.2, et les conditions du genre
    // « 3e curseur à partir de E2 ». Seul l'exposant bouge.
    const exposant = Math.max(0, k - 1);
    const modeDoux = state.subscribers < 100;
    let window_mult = Math.pow(0.9, exposant);
    if (modeDoux)
        window_mult *= 1.3;
    if (state.flags.accessibility_tolerance)
        window_mult *= 1.3;
    const capTolerance = capEchelle(state);
    return {
        k,
        tempo_mult: Math.pow(1.08, exposant),
        window_mult,
        cuts_mult: Math.pow(1.2, exposant),
        score_cap: stage === undefined
            ? capTolerance
            : Math.min(capTolerance, plafondCompetence(state, stage, content, options)),
    };
}
/**
 * ⟦A-33⟧ **Ce que la chaîne retient d'un geste** — la seule porte par laquelle un score joué
 * devient un score qui compte.
 *
 * Trois étages, dans cet ordre, et l'ordre porte tout le sens :
 *
 *  1. **le plafond de compétence** (`plafondCompetence`) — ce que la chaîne sait exploiter ;
 *  2. **le surpassement** (`K_SURPASSEMENT`) — ce qu'elle arrive quand même à tirer d'un geste
 *     au-dessus de son niveau. À `K = 0` cet étage n'existe pas et on retrouve la coupe ;
 *  3. **la borne dure de l'échelle** (`capEchelle`) — 100, ou 85 en tolérance. Elle passe en
 *     **dernier**, donc elle gagne : le surpassement ne rend jamais la tolérance caduque, et
 *     aucun score ne sort de `[0;100]`.
 *
 * ⟦A-10⟧ **Neutralité prouvée par l'arithmétique, pas par l'intention.** À `K = 0`, pour tout
 * `v` et tout état : cette fonction rend `clamp(v, 0, min(capEchelle, plafondCompetence))`,
 * qui est **exactement** l'expression qu'⟦A-9⟧ avait écrite dans `scoresEffectifs`. Vérifié en
 * plus par mesure : la campagne `simulate.ts --runs 25` rend un CSV identique au caractère près.
 */
export function scoreEffectifAtelier(score, state, stage, content, options = {}) {
    const plafond = plafondCompetence(state, stage, content, options);
    const compresse = score > plafond ? plafond + (score - plafond) * K_SURPASSEMENT : score;
    return Math.min(capEchelle(state), Math.max(0, compresse));
}
// ─────────────────────────────────────────────────────────────────────────────
// §2.3 pas 10 — recalcul de progression
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Palier qui déclenche « Le Grand Saut » (**avenant A-1**) : c'est là que le joueur
 * démissionne, que ses parents lui avancent un capital, et que la couche argent — revenus
 * ET charges — s'active. Avant, la jauge Argent est inactive : 0 ₵, aucune charge, aucune
 * faillite possible.
 */
export const PALIER_GRAND_SAUT = 1000;
/**
 * Recalcule niveau et paliers d'après l'état courant, crédite les PC et émet les
 * `EngineEvent` correspondants. MUTE `state` — appelé depuis la file de résolution, qui
 * travaille déjà sur un clone (§2.3).
 *
 * Ordre : niveaux d'abord (l'XP a été versée par les courbes), puis paliers d'abonnés.
 * Chaque montée de niveau donne +1 PC, chaque palier +2 PC (§9.3).
 */
export function appliquerProgression(state, events = [], 
/**
 * ⟦C60 · A-177⟧ Les interrupteurs du ménage : seul le capital du Grand Saut en dépend. Absent ⇒
 * le jeu d'avant (600 ₵), au caractère près.
 */
sys = SYSTEMES_TOUS_ACTIFS, 
/**
 * ⟦C62 · lot moteur · A-179⟧ Le PIC d'abonnés de l'action en cours (`HookContext.picAbonnes`). Il ne sert
 * qu'au palier du MILLION : la victoire se grave au franchissement, donc le palier qui la porte
 * (plaque d'or, +2 PC, écran « objectif atteint ») aussi, même si la nuit a repris les abonnés avant
 * ce pas. Tous les autres paliers se lisent, comme avant, sur les abonnés de la fin. Absent ou
 * inférieur ⇒ le jeu d'avant, au caractère près.
 */
picAbonnes = 0) {
    const resultat = {
        niveaux_gagnes: 0,
        paliers_franchis: [],
        pc_credites: 0,
        grand_saut: false,
    };
    // — Niveaux (§9.1) —
    const niveauCible = niveauPourXp(state.xp);
    while (state.level < niveauCible) {
        state.level += 1;
        state.pc += PC_PAR_NIVEAU;
        resultat.niveaux_gagnes += 1;
        resultat.pc_credites += PC_PAR_NIVEAU;
        events.push({
            kind: "level_up",
            data: { level: state.level, pc_gagnes: PC_PAR_NIVEAU, xp: state.xp },
        });
    }
    // — Paliers (§9.2) — jamais redescendus : un palier franchi le reste (PLAN §4.13).
    for (const palier of TOUS_PALIERS) {
        // Les paliers post-1M n'existent QUE dans le mode 100 %, ouvert par `continuer_apres_rue`
        // (PLAN §4.13). Sans cette garde, un joueur qui n'a pas encore choisi de continuer les
        // franchissait en silence — et « continuer après la rue » n'aurait rien ouvert du tout.
        if (!state.flags.apres_la_rue && PALIERS_POST_1M.includes(palier)) {
            continue;
        }
        const atteints = palier === PALIER_MILLION ? Math.max(state.subscribers, picAbonnes) : state.subscribers;
        if (atteints < palier)
            continue;
        if (state.milestones_reached.includes(palier))
            continue;
        state.milestones_reached.push(palier);
        state.pc += PC_PAR_PALIER;
        state.counters.last_milestone_h = state.clock_h;
        resultat.paliers_franchis.push(palier);
        resultat.pc_credites += PC_PAR_PALIER;
        events.push({
            kind: "milestone",
            data: {
                palier,
                pc_gagnes: PC_PAR_PALIER,
                // Cosmétique, données portées par l'event (§9.2) : le moteur ne dessine rien.
                plaque: palier === 100_000 ? "bouton_argent" : palier === 1_000_000 ? "bouton_or" : null,
                exigence_k: exigenceK(state),
            },
        });
        // — Avenant A-1 « Le Grand Saut » : le palier 1 000 active la couche argent —
        if (palier === PALIER_GRAND_SAUT && !state.flags.grand_saut_done) {
            state.flags.grand_saut_done = true;
            // ⟦C60 · A-177⟧ Le capital est une valeur de CONTENU (`meta.systemes.capital_grand_saut`) :
            // 600 par défaut (le jeu d'avant), `0` quand le ménage coupe le loyer, le palier de 1 000
            // reste un palier (PC, plaque, ouverture de l'argent) et ne verse plus rien.
            state.money += sys.capital_grand_saut;
            resultat.grand_saut = true;
            events.push({
                kind: "warning",
                data: {
                    scripted: "grand_saut",
                    capital: sys.capital_grand_saut,
                    charges_par_jour: sys.charges ? CHARGES_FIXES_PAR_JOUR : 0,
                    // La narration (démission du Tasty Crusty, soutien des parents) est portée par
                    // l'événement scripté du contenu ; le moteur n'écrit pas de texte de lore.
                },
            });
        }
    }
    return resultat;
}
/** Crédite les PC d'un événement majeur survécu (§9.3). MUTE `state`. */
export function crediterEvenementMajeur(state, events = []) {
    state.pc += PC_PAR_EVENEMENT_MAJEUR;
    events.push({ kind: "event_resolved", data: { pc_gagnes: PC_PAR_EVENEMENT_MAJEUR, major: true } });
}
/** Crédite les PC d'une vidéo jalon réussie (§9.3, score ≥ 60). MUTE `state`. */
export function crediterJalon(state, executionScore) {
    const reussi = executionScore >= SEUIL_JALON_REUSSI;
    if (reussi)
        state.pc += PC_PAR_JALON;
    return reussi;
}
//# sourceMappingURL=progress.js.map